import { useState } from 'react';
import { LucideNetwork, ChevronRight, File, Folder, FolderOpen } from 'lucide-react';
import type { FileType } from '../hooks/use-chat';
import { useFileDragDrop } from '@/hooks/use-file-drag-drop';
import { FileContextMenu, useFileContextMenu } from '@/components/file-context-menu';
import {
  NewFileDialog,
  NewFolderDialog,
  RenameDialog,
  DeleteDialog,
  PropertiesDialog
} from '@/components/file-management-dialogs';
import { LocalHistoryPanel } from '@/components/local-history-panel';
import { useLocalHistory } from '@/hooks/use-local-history';
import clsx from 'clsx';

interface FileTreeItem {
	name: string;
	type: 'file' | 'folder';
	filePath: string;
	children?: { [key: string]: FileTreeItem };
	file?: FileType;
}

export function FileTreeItem({
	item,
	level = 0,
	currentFile,
	onFileClick,
	fileDragHandlers,
	folderDropHandlers,
	dragOverTarget,
	onContextMenu,
}: {
	item: FileTreeItem;
	level?: number;
	currentFile: FileType | undefined;
	onFileClick: (file: FileType) => void;
	fileDragHandlers: {
		onDragStart: (e: React.DragEvent, file: FileType) => void;
		onDragEnd: (e: React.DragEvent) => void;
	};
	folderDropHandlers: (folderPath: string) => {
		onDragEnter: (e: React.DragEvent) => void;
		onDragOver: (e: React.DragEvent) => void;
		onDragLeave: (e: React.DragEvent) => void;
		onDrop: (e: React.DragEvent) => void;
	};
	dragOverTarget?: string;
	onContextMenu: (e: React.MouseEvent, file?: FileType, path?: string, isFolder?: boolean) => void;
}) {
	const [isExpanded, setIsExpanded] = useState(true);
	const isCurrentFile = currentFile?.filePath === item.filePath;
	const isDragOver = dragOverTarget === item.filePath;

	if (item.type === 'file' && item.file) {
		return (
			<button
				draggable
				onClick={() => onFileClick(item.file!)}
				onContextMenu={(e) => onContextMenu(e, item.file, item.filePath, false)}
				onDragStart={(e) => fileDragHandlers.onDragStart(e, item.file!)}
				onDragEnd={fileDragHandlers.onDragEnd}
				className={`flex items-center w-full gap-2 py-1 px-3 transition-colors text-sm cursor-pointer ${
					isCurrentFile
						? 'text-brand bg-zinc-100'
						: 'text-text-primary/80 hover:bg-accent hover:text-text-primary'
				}`}
				style={{ paddingLeft: `${level * 12 + 12}px` }}
			>
				<File className="size-3" />
				<span className="flex-1 text-left truncate">{item.name}</span>
			</button>
		);
	}

	const folderHandlers = folderDropHandlers(item.filePath);

	return (
		<div>
			<button
				onClick={() => setIsExpanded(!isExpanded)}
				onContextMenu={(e) => onContextMenu(e, undefined, item.filePath, true)}
				{...folderHandlers}
				className={clsx(
					"flex items-center gap-2 py-1 px-3 transition-colors text-sm text-text-primary/80 hover:bg-accent hover:text-text-primary w-full",
					isDragOver && "bg-blue-100 dark:bg-blue-900/30 border-l-2 border-blue-500"
				)}
				style={{ paddingLeft: `${level * 12 + 12}px` }}
			>
				<ChevronRight
					className={clsx(
						'size-3 transition-transform duration-200 ease-in-out',
						isExpanded && 'rotate-90',
					)}
				/>
				{isExpanded ? (
					<FolderOpen className="size-3" />
				) : (
					<Folder className="size-3" />
				)}
				<span className="flex-1 text-left truncate">{item.name}</span>
			</button>
			{isExpanded && item.children && (
				<div>
					{Object.values(item.children).map((child) => (
						<FileTreeItem
							key={child.filePath}
							item={child}
							level={level + 1}
							currentFile={currentFile}
							onFileClick={onFileClick}
							fileDragHandlers={fileDragHandlers}
							folderDropHandlers={folderDropHandlers}
							dragOverTarget={dragOverTarget}
							onContextMenu={onContextMenu}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function buildFileTree(files: FileType[]): FileTreeItem[] {
	const root: { [key: string]: FileTreeItem } = {};

	files.forEach((file) => {
		const parts = file.filePath.split('/');
		let currentLevel: { [key: string]: FileTreeItem } = root;

		for (let i = 0; i < parts.length - 1; i++) {
			const part = parts[i];
			if (!currentLevel[part]) {
				currentLevel[part] = {
					name: part,
					type: 'folder',
					filePath: parts.slice(0, i + 1).join('/'),
					children: {},
				};
			}
			if (!currentLevel[part].children) {
				currentLevel[part].children = {};
			}
			currentLevel = currentLevel[part].children;
		}

		const fileName = parts[parts.length - 1];
		currentLevel[fileName] = {
			name: fileName,
			type: 'file',
			filePath: file.filePath,
			file: file,
		};
	});

	return Object.values(root);
}

export function FileExplorer({
	files,
	bootstrapFiles,
	currentFile,
	onFileClick,
	onFilesDropped,
	onFileMove,
	onFolderMove,
	onNewFile,
	onNewFolder,
	onRename,
	onDelete,
	onDownload,
	onUpdateFile,
}: {
	files: FileType[];
	bootstrapFiles: FileType[];
	currentFile: FileType | undefined;
	onFileClick: (file: FileType) => void;
	onFilesDropped?: (files: File[]) => void;
	onFileMove?: (sourceFile: FileType, targetPath: string) => void;
	onFolderMove?: (sourcePath: string, targetPath: string) => void;
	onNewFile?: (fileName: string, parentPath?: string) => void;
	onNewFolder?: (folderName: string, parentPath?: string) => void;
	onRename?: (file: FileType, newName: string) => void;
	onDelete?: (file: FileType) => void;
	onDownload?: (file: FileType) => void;
	onUpdateFile?: (filePath: string, updates: Partial<FileType>) => void;
}) {
	const fileTree = buildFileTree([...bootstrapFiles, ...files]);

	const {
		isDragging,
		dragOverTarget,
		dragHandlers,
		fileDragHandlers,
		folderDropHandlers,
	} = useFileDragDrop({
		onFilesDropped,
		onFileMove,
		onFolderMove,
		allowFileUpload: !!onFilesDropped,
		allowFileReorganization: !!(onFileMove || onFolderMove),
		accept: ['text/*', 'application/json', 'application/javascript', 'application/typescript'],
	});

	// Context menu state
	const contextMenu = useFileContextMenu();

	// Local history hook
	const localHistory = useLocalHistory();

	// Dialog states
	const [newFileDialog, setNewFileDialog] = useState({ isOpen: false, parentPath: undefined as string | undefined });
	const [newFolderDialog, setNewFolderDialog] = useState({ isOpen: false, parentPath: undefined as string | undefined });
	const [renameDialog, setRenameDialog] = useState({ isOpen: false, file: undefined as FileType | undefined });
	const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, file: undefined as FileType | undefined });
	const [propertiesDialog, setPropertiesDialog] = useState({ isOpen: false, file: undefined as FileType | undefined });
	const [historyPanel, setHistoryPanel] = useState({ isOpen: false, file: undefined as FileType | undefined });

	// Context menu handlers
	const handleNewFile = (fileName: string, parentPath?: string) => {
		onNewFile?.(fileName, parentPath);
	};

	const handleNewFolder = (folderName: string, parentPath?: string) => {
		onNewFolder?.(folderName, parentPath);
	};

	const handleRename = (file: FileType, newName: string) => {
		onRename?.(file, newName);
	};

	const handleDelete = (file: FileType) => {
		onDelete?.(file);
	};

	const handleEdit = (file: FileType) => {
		onFileClick(file);
	};

	const handleCopy = (file: FileType) => {
		// Copy file path to clipboard
		navigator.clipboard.writeText(file.filePath);
	};

	const handleCut = (file: FileType) => {
		// TODO: Implement cut functionality
		console.log('Cut file:', file.filePath);
	};

	const handleViewHistory = (file: FileType) => {
		setHistoryPanel({ isOpen: true, file });
	};

	const handleCompare = (file: FileType) => {
		// TODO: Implement compare/diff functionality
		console.log('Compare file:', file.filePath);
	};

	const handleRestoreSnapshot = (snapshot: { content: string }) => {
		if (historyPanel.file) {
			onUpdateFile?.(historyPanel.file.filePath, { fileContents: snapshot.content });
			setHistoryPanel({ isOpen: false, file: undefined });
		}
	};

	const handleDeleteSnapshot = (snapshotId: string) => {
		if (historyPanel.file) {
			localHistory.deleteSnapshot(historyPanel.file.filePath, snapshotId);
		}
	};

	const handleProperties = (file: FileType) => {
		setPropertiesDialog({ isOpen: true, file });
	};

	const handleDownloadFile = (file: FileType) => {
		onDownload?.(file);
	};

	const handlePreview = (file: FileType) => {
		// TODO: Implement preview functionality
		console.log('Preview file:', file.filePath);
	};

	return (
		<div
			className={clsx(
				"w-full max-w-[200px] bg-bg-3 border-r border-text/10 h-full overflow-y-auto relative",
				isDragging && "ring-2 ring-blue-500 ring-inset"
			)}
			{...dragHandlers}
		>
			{isDragging && (
				<div className="absolute inset-0 flex items-center justify-center bg-blue-50 dark:bg-blue-900/20 backdrop-blur-sm z-50 pointer-events-none">
					<p className="text-blue-600 dark:text-blue-400 font-medium">Drop files here</p>
				</div>
			)}

			<div className="p-2 px-3 text-sm flex items-center gap-1 text-text-primary/50 font-medium">
				<LucideNetwork className="size-4" />
				Files
			</div>
			<div className="flex flex-col">
				{fileTree.map((item) => (
					<FileTreeItem
						key={item.filePath}
						item={item}
						currentFile={currentFile}
						onFileClick={onFileClick}
						fileDragHandlers={fileDragHandlers}
						folderDropHandlers={folderDropHandlers}
						dragOverTarget={dragOverTarget}
						onContextMenu={contextMenu.openMenu}
					/>
				))}
			</div>

			{/* Context Menu */}
			<FileContextMenu
				isOpen={contextMenu.isOpen}
				position={contextMenu.position}
				targetFile={contextMenu.targetFile}
				targetPath={contextMenu.targetPath}
				isFolder={contextMenu.isFolder}
				onClose={contextMenu.closeMenu}
				onNewFile={(parentPath) => setNewFileDialog({ isOpen: true, parentPath })}
				onNewFolder={(parentPath) => setNewFolderDialog({ isOpen: true, parentPath })}
				onRename={(file) => setRenameDialog({ isOpen: true, file })}
				onDelete={(file) => setDeleteDialog({ isOpen: true, file })}
				onEdit={handleEdit}
				onCopy={handleCopy}
				onCut={handleCut}
				onViewHistory={handleViewHistory}
				onCompare={handleCompare}
				onProperties={handleProperties}
				onDownload={handleDownloadFile}
				onPreview={handlePreview}
			/>

			{/* Dialogs */}
			<NewFileDialog
				isOpen={newFileDialog.isOpen}
				parentPath={newFileDialog.parentPath}
				onClose={() => setNewFileDialog({ isOpen: false, parentPath: undefined })}
				onConfirm={handleNewFile}
			/>

			<NewFolderDialog
				isOpen={newFolderDialog.isOpen}
				parentPath={newFolderDialog.parentPath}
				onClose={() => setNewFolderDialog({ isOpen: false, parentPath: undefined })}
				onConfirm={handleNewFolder}
			/>

			<RenameDialog
				isOpen={renameDialog.isOpen}
				file={renameDialog.file!}
				onClose={() => setRenameDialog({ isOpen: false, file: undefined })}
				onConfirm={handleRename}
			/>

			<DeleteDialog
				isOpen={deleteDialog.isOpen}
				file={deleteDialog.file!}
				onClose={() => setDeleteDialog({ isOpen: false, file: undefined })}
				onConfirm={handleDelete}
			/>

			<PropertiesDialog
				isOpen={propertiesDialog.isOpen}
				file={propertiesDialog.file!}
				onClose={() => setPropertiesDialog({ isOpen: false, file: undefined })}
			/>

			{historyPanel.isOpen && historyPanel.file && (
				<LocalHistoryPanel
					filePath={historyPanel.file.filePath}
					fileHistory={localHistory.getFileHistory(historyPanel.file.filePath)}
					currentContent={historyPanel.file.fileContents}
					onClose={() => setHistoryPanel({ isOpen: false, file: undefined })}
					onRestore={handleRestoreSnapshot}
					onDelete={handleDeleteSnapshot}
				/>
			)}
		</div>
	);
}
